import React, { FC, useContext, useMemo } from 'react';
import { Formik } from 'formik';
import { Asset, FilterIcon } from '@clearcover/atlas';

import { Row, Column, FormInput } from 'Shared/components';
import { ClearFilterButton } from 'Shared/components/ClearFilterButtons';
import {
  UserTypeaheadResponse,
  getFetchUsersConfig,
} from 'Admin/services/endpoints/PostAttributionTypeahead';
import { useLazyBaseApi } from 'Shared/services/Api';
import { PartnerContext } from 'Admin/utils';
import {
  STATE_OPTIONS,
  INDEPENDENT_AGENT_STATES,
} from 'Shared/constants/states';

import { Typeahead } from '../../components/Typeahead';
import { TypeaheadResult } from '../../components/TypeaheadResult';
import { useStore } from '../../store';

import styles from './styles.module.css';

export const Filters: FC = () => {
  const { currentPartner } = useContext(PartnerContext);
  const [state, dispatch] = useStore();

  const [{ loading: loadingUsers }, fetchUsers] = useLazyBaseApi<
    UserTypeaheadResponse
  >({ url: `/partners/${currentPartner?.id}/portal_users` });

  const multiSelectFilters = useMemo(
    () => [
      {
        fieldName: 'State',
        field: 'policy_state',
        availableFilters: INDEPENDENT_AGENT_STATES.map(agentState => ({
          display: `${agentState} - ${STATE_OPTIONS[
            agentState
          ]?.toUpperCase()}`,
          value: agentState,
        })),
        getFilterPayload: (text: Array<string>) => text,
      },
    ],
    []
  );

  const prefix = useMemo(() => new Date().getTime().toString(), []);

  if (!currentPartner) return null;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        [prefix]: {
          policyNumber: state.filters.policyNumber,
          typeaheadUserName: state.from.agent?.name || '',
        },
      }}
      onSubmit={values => {
        // on 'enter' key press or blur set the reducer state for policy number
        dispatch({
          type: 'policies.filters.policyNumber.update',
          value: values[prefix].policyNumber,
        });
      }}
    >
      {({
        dirty,
        values,
        setFieldValue,
        handleChange,
        handleSubmit,
        submitForm,
      }) => (
        <form onSubmit={handleSubmit} data-testid="reattribution-filters-form">
          <Row>
            <Column variant="half">
              <FormInput
                className={styles.filterInput}
                name={`${prefix}.policyNumber`}
                value={values[prefix].policyNumber}
                label="Search policy number"
                variant="search"
                onChange={handleChange}
                onBlur={() => {
                  // submit on blur
                  dirty && submitForm();
                }}
                onIconClick={() => {
                  handleSubmit();
                }}
              />
            </Column>
            <Column variant="half">
              <Typeahead<UserTypeaheadResponse>
                floating
                className={styles.filterInput}
                name={`${prefix}.typeaheadUserName`}
                label="Search Agent Name or Email"
                debounce={200}
                value={values[prefix].typeaheadUserName}
                setValue={value => {
                  setFieldValue(`${prefix}.typeaheadUserName`, value);
                }}
                loading={loadingUsers}
                lazyRequest={fetchUsers}
                getLazyRequestConfig={getFetchUsersConfig}
                onSelect={(selectedId, options) => {
                  const selectedOption = options.find(
                    option => option.id === selectedId
                  );

                  if (selectedOption) {
                    // set selection and reinitialize formik state with new results
                    dispatch({
                      type: 'from.agent.set',
                      value: selectedOption,
                      filtered: true,
                    });
                  }
                }}
                isClearable={!!state.from.agent}
                onClear={() => {
                  setFieldValue(`${prefix}.typeaheadUserName`, '');
                  dispatch({
                    type: 'from.agent.set',
                    value: null,
                  });
                }}
                transformOptions={(options: UserTypeaheadResponse) =>
                  options.reduce((acc, current) => {
                    return {
                      ...acc,
                      [current.id]: (
                        <TypeaheadResult
                          primaryResult={current.name}
                          secondaryResult={current.email}
                        />
                      ),
                    };
                  }, {})
                }
              />
            </Column>
          </Row>
          <Row>
            <Column className={styles.filterRow}>
              <span className={styles.filterHeader}>
                Filters
                <Asset
                  className={styles.filterIcon}
                  icon={FilterIcon}
                  height={16}
                  width={16}
                  description="Filter"
                />
              </span>
              {multiSelectFilters.map(filter => {
                return (
                  <FormInput
                    key={filter.field}
                    variant="multiselect"
                    items={filter.availableFilters.map(
                      ({ display, value }) => ({
                        key: value,
                        label: display,
                        selected: state.filters.states.includes(value),
                      })
                    )}
                    label={filter.fieldName}
                    onChangeSelection={item => {
                      if (item) {
                        dispatch({
                          type: 'policies.filters.state.select',
                          value: item.key,
                        });
                      }
                    }}
                  />
                );
              })}
              {/* hidden button to submit */}
              <button type="submit" />
              <ClearFilterButton
                onClick={() => {
                  dispatch({
                    type: 'policies.filters.reset',
                  });
                }}
                data-testid="reattribution-clear-filters"
                disabled={
                  !state.from.agent &&
                  !state.filters.policyNumber &&
                  !state.filters.states.length
                }
              >
                Clear Filters and Search
              </ClearFilterButton>
            </Column>
          </Row>
        </form>
      )}
    </Formik>
  );
};
